<template>
  <UiDialogBase
    :model-value="modelValue"
    :scrollable="scrollable"
    :width="width"
    :max-width="maxWidth"
    :min-width="minWidth"
    :eager="eager"
    :height="height"
    :max-height="maxHeight"
    :min-height="minHeight"
    class="ui-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <UiForm @submit="$emit('submit')">
      <UiCard>
        <UiCardTitle v-if="title" class="dialog__title">
          <UiText variant="subtitle-2" color="text-primary">
            <AppTranslate :identifier="title" />
          </UiText>
          <UiButton
            variant="text"
            color="button-surface"
            icon="$close"
            density="compact"
            class="dialog__close"
            data-test="dialog-close-button"
            @click="$emit('update:modelValue', false)"
          />
          <slot name="title" />
        </UiCardTitle>
        <UiCardText
          class="dialog__content"
          :class="{ 'dialog__content--empty': !$slots['default'] }"
          @scroll.passive="onContentScroll"
        >
          <UiContainer v-if="loading" class="dialog__container">
            <UiSpinner :size="30" class="dialog__spinner" />
          </UiContainer>
          <slot />
        </UiCardText>
        <UiCardActions v-if="$slots['actions']" class="dialog__actions">
          <slot name="actions" />
        </UiCardActions>
      </UiCard>
    </UiForm>
  </UiDialogBase>
</template>

<script setup lang="ts">
import {
  UiButton,
  UiText,
  UiCardActions,
  UiCardText,
  UiCardTitle,
  UiCard,
  UiForm,
} from '~/ui';

import UiDialogBase from './UiDialogBase.vue';
import UiSpinner from '~/ui/UiSpinner/UiSpinner.vue';
import UiContainer from '~/ui/UiContainer/UiContainer.vue';
import { AppTranslate } from '~/infrastructure';

export interface Props {
  modelValue: boolean;
  title?: string | { key: string; params: any };
  scrollable?: boolean;
  width?: string | number;
  maxWidth?: string | number;
  minWidth?: string | number;
  loading?: boolean;
  fullscreen?: boolean;
  eager?: boolean;
  height?: string | number;
  maxHeight?: string | number;
  minHeight?: string | number;
}
withDefaults(defineProps<Props>(), {
  scrollable: false,
  width: '500px',
  maxWidth: undefined,
  minWidth: undefined,
  loading: false,
  eager: false,
  height: undefined,
  maxHeight: undefined,
  title: undefined,
  minHeight: undefined,
});
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'submit'): void;
  (e: 'scrolledToTheEnd'): void;
}>();

const onContentScroll = (event: Event): void => {
  const target = event.target as HTMLElement | null;

  if (target === null) {
    return;
  }

  const isScrolledToBottom =
    target.scrollTop + target.clientHeight >= target.scrollHeight;

  if (isScrolledToBottom) {
    emit('scrolledToTheEnd');
  }
};
</script>

<style scoped lang="scss">
.ui-dialog {
  .dialog__actions {
    padding: 0 32px 32px !important;
    min-height: auto;
  }

  .dialog__content {
    position: relative;
    padding: 25px 32px 27px !important;

    &--empty {
      padding: 15px 32px 17px !important;
    }
  }

  .dialog__container {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.6);
    z-index: 2000;
  }

  .dialog__close {
    position: absolute;
    right: 20px;
    top: 35px;
    min-height: inherit;
    height: 30px !important;
    margin-top: -8px;
  }

  .dialog__title {
    white-space: inherit;
    padding: 32px 64px 0 32px;
  }

  @include bp-down(sm) {
    .dialog__actions {
      padding: var(--size-lg) !important;
      min-height: auto;
      border-top: 1px solid rgb(var(--v-theme-border-primary));
    }

    .dialog__content {
      position: relative;
      padding: var(--size-lg) !important;
    }

    .dialog__title {
      padding: 24px 48px 0 24px;
    }

    .dialog__close {
      right: 12px;
      top: 28px;
    }
  }
}
</style>
